import { Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { CSSProperties, ReactNode } from "react";

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export type JsonDataViewProps = {
  data: JsonValue | Record<string, unknown> | unknown[] | undefined;
  title?: string;
  emptyText?: string;
};

const sectionStyle = (depth: number, isRoot: boolean): CSSProperties => ({
  padding: isRoot ? 20 : 16,
  borderRadius: 8,
  backgroundColor: isRoot ? "#ffffff" : depth % 2 === 0 ? "#fafafa" : "#ffffff",
});

const labelStyle: CSSProperties = {
  color: "rgba(0, 0, 0, 0.6)",
  fontFamily: "Monaco, Menlo, Consolas, monospace",
  fontSize: 13,
  letterSpacing: "0.02em",
};

const objectRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "180px minmax(0, 1fr)",
  gap: 12,
  alignItems: "start",
};

const isPrimitive = (value: unknown): value is JsonPrimitive =>
  value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const renderPrimitive = (value: unknown): ReactNode => {
  if (typeof value === "string") {
    return (
      <Typography variant="body2" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    );
  }

  if (typeof value === "number") {
    return <Chip label={String(value)} size="small" color="primary" variant="outlined" />;
  }

  if (typeof value === "boolean") {
    return <Chip label={value ? "true" : "false"} size="small" color={value ? "success" : "default"} />;
  }

  if (value === null) {
    return <Chip label="null" size="small" variant="outlined" />;
  }

  return (
    <Typography variant="body2" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {String(value)}
    </Typography>
  );
};

const renderHeader = (label: string | undefined, depth: number) => {
  if (!label) {
    return null;
  }

  return (
    <Typography variant={depth === 0 ? "h6" : "subtitle2"} style={{ marginBottom: 12 }}>
      {label}
    </Typography>
  );
};

const JsonNode = ({
  value,
  label,
  depth,
  isRoot = false,
}: {
  value: unknown;
  label?: string;
  depth: number;
  isRoot?: boolean;
}) => {
  if (isPrimitive(value)) {
    return (
      <Paper variant="outlined" style={sectionStyle(depth, isRoot)}>
        {renderHeader(label, depth)}
        {renderPrimitive(value)}
      </Paper>
    );
  }

  if (Array.isArray(value)) {
    const isPrimitiveArray = value.every(isPrimitive);

    return (
      <Paper variant="outlined" style={sectionStyle(depth, isRoot)}>
        {renderHeader(label, depth)}
        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Empty list
          </Typography>
        ) : isPrimitiveArray ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {value.map((item, index) => (
              <div key={`${label || "item"}-${index}`}>{renderPrimitive(item)}</div>
            ))}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {value.map((item, index) => (
              <JsonNode
                key={`${label || "item"}-${index}`}
                value={item}
                label={`Item ${index + 1}`}
                depth={depth + 1}
              />
            ))}
          </Stack>
        )}
      </Paper>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);

    return (
      <Paper variant="outlined" style={sectionStyle(depth, isRoot)}>
        {renderHeader(label, depth)}
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Empty object
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {entries.map(([entryKey, entryValue]) => (
              <div key={`${label || "root"}-${entryKey}`} style={objectRowStyle}>
                <Typography component="div" style={labelStyle}>
                  {entryKey}
                </Typography>
                <JsonNode value={entryValue} depth={depth + 1} />
              </div>
            ))}
          </Stack>
        )}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" style={sectionStyle(depth, isRoot)}>
      {renderHeader(label, depth)}
      <Typography variant="body2" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {String(value)}
      </Typography>
    </Paper>
  );
};

export const JsonDataView = ({ data, title, emptyText = "No data available" }: JsonDataViewProps) => {
  if (data === undefined) {
    return (
      <div>
        <Paper variant="outlined" style={sectionStyle(0, true)}>
          {renderHeader(title, 0)}
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div>
      <JsonNode value={data} label={title} depth={0} isRoot />
    </div>
  );
};
